import React, { Fragment, type HTMLProps, type PropsWithChildren, type ReactNode, useState } from 'react';
import { styled } from 'styled-components';
import { FuuzeButton } from './fuuze-button.js';
import { FuuzeDivider } from './fuuze-divider.js';
import { FlexColumn } from './fuuze-flex.js';

export type FuuzeContextMenuProps = PropsWithChildren<{
  open?: boolean;
  Trigger: ReactNode;
  position?: { x: number; y: number } | null;
  menuElements: {
    label: string;
    callback: () => void;
  }[];
}> &
  HTMLProps<HTMLDivElement>;

export function FuuzeContextMenu({ open, Trigger, children, position, menuElements, ...props }: FuuzeContextMenuProps) {
  const [menuVisible, setMenuVisible] = useState(open || false);
  const [prevMenuVisible, setPrevMenuVisible] = useState(menuVisible);
  if (open !== undefined && open !== prevMenuVisible) {
    setPrevMenuVisible(open);
    setMenuVisible(open);
  }

  const [menuPosition, setMenuPosition] = useState({ x: position?.x || 0, y: position?.y || 0 });
  const [prevPosition, setPrevPosition] = useState(menuPosition);
  if (position && (position.x !== prevPosition.x || position.y !== prevPosition.y)) {
    setPrevPosition(position);
    setMenuPosition(position);
  }

  const handleContextMenu = e => {
    e.preventDefault();
    setMenuVisible(true);
    setMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleClickOutside = () => {
    setMenuVisible(false);
  };

  return (
    <div role='menu' onContextMenu={handleContextMenu} onClick={handleClickOutside} {...props}>
      {Trigger}
      {menuVisible && (
        <Wrapper $top={menuPosition.y} $left={menuPosition.x}>
          {menuElements.map(({ label, callback }, i, array) => (
            <Fragment key={`${label}-${i}`}>
              <MenuButton flat onClick={callback}>
                {label}
              </MenuButton>
              {i !== array.length && <FuuzeDivider />}
            </Fragment>
          ))}
        </Wrapper>
      )}
    </div>
  );
}

const Wrapper = styled(FlexColumn)<{ $top?: number; $left?: number }>`
  position: absolute;
  top: ${props => props.$top || 0}px;
  left: ${props => props.$left || 0}px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.dark.dark800};
  z-index: 100;
  gap: 0;
  border-radius: 4px;
`;

const MenuButton = styled(FuuzeButton)`
  font-size: ${props => props.theme.typography.body2.fontSize}px;
`;
